import axios from 'axios';
import {API_BASE_URL} from './authApi';

export interface FeedAuthor {
  _id: string;
  isFollowing: boolean;
  profile?: {
    name?: string;
    profilePicture?: string;
  };
}

interface HomeReelAuthor {
  id: string;
  name: string;
  profilePicture?: string;
}

interface FeedCommentAuthor {
  id: string;
  name: string;
  profilePicture?: string;
}

export type PostReactionType = 'like' | 'laugh' | 'cry' | 'love' | 'celebrate' | 'fire';

export interface FeedPostReactionSummaryItem {
  type: PostReactionType;
  count: number;
}

interface FeedPostBase {
  _id: string;
  authorId: FeedAuthor | null;
  content: string;
  mediaUrls?: string[];
  commentCount: number;
  likeCount: number;
  hasLiked: boolean;
  reactionCount?: number;
  reactionSummary?: FeedPostReactionSummaryItem[];
  viewerReaction?: PostReactionType | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeedOriginalPost extends FeedPostBase {
  postType: 'original';
  originalPost: null;
}

export interface FeedPost extends FeedPostBase {
  postType: 'original' | 'repost';
  originalPost: FeedOriginalPost | null;
}

export interface FeedComment {
  id: string;
  content: string;
  createdAt: string;
  author: FeedCommentAuthor;
}

export interface GetPostsOptions {
  cursor?: string;
  limit?: number;
  topic?: string;
}

export type HomeReelFeedView = 'all' | 'featured' | 'newest' | 'popular' | 'mostDiscussed';

export interface GetHomeReelsOptions {
  cursor?: string;
  limit?: number;
  view?: HomeReelFeedView;
  search?: string;
  topic?: string;
}

export interface HomeReel {
  reelId: string;
  videoId: string;
  title: string;
  videoUrl: string;
  thumbnailUrl?: string;
  viewCount: number;
  likeCount: number;
  hasLiked: boolean;
  commentCount: number;
  durationSeconds?: number;
  createdAt: string;
  isFeatured: boolean;
  author: HomeReelAuthor;
}

export interface ReelShareRecipient {
  id: string;
  name: string;
  profilePicture?: string;
  organization: {id: string; name: string};
  role: 'coach' | 'recruiter';
  alreadyShared: boolean;
}

export interface ReelShareDelivery {
  notificationId: string;
  recipientId: string;
  reelId: string;
  createdAt: string;
  alreadyShared: boolean;
}

interface FeedResponse {
  success: boolean;
  posts: FeedPost[];
  nextCursor?: string;
}

interface GetPostResponse {
  success: boolean;
  post: FeedPost;
}

interface HomeReelsResponse {
  success: boolean;
  reels: HomeReel[];
  nextCursor?: string;
}

interface GetHighlightReelResponse {
  success: boolean;
  reel: HomeReel;
}

interface FeedCommentsResponse {
  success: boolean;
  comments: FeedComment[];
}

export interface HomeStats {
  followerCount: number;
  followingCount: number;
  postCount: number;
  newMessageCount: number;
}

export interface TrendingTopic {
  key: string;
  label: string;
  score: number;
  contentCount: number;
  metrics: {views: number; reactions: number; comments: number; reposts: number};
  lastActivityAt: string;
}

interface HomeStatsResponse {
  success: boolean;
  stats: HomeStats;
}

interface TrendingTopicsResponse {
  success: boolean;
  generatedAt: string;
  window: {days: number; startAt: string; endAt: string};
  topics: TrendingTopic[];
}

export interface CreatePostPayload {
  content: string;
  uploadSessionIds?: string[];
}

export interface RepostPostPayload {
  message?: string;
}

export interface CreateHighlightReelPayload {
  uploadSessionId: string;
  parts: CompletedMultipartUploadPart[];
  title: string;
  description?: string;
  isFeatured?: boolean;
  durationSeconds?: number;
}

interface HighlightReelRecord {
  _id?: string;
  videoId: string;
  title: string;
  description: string;
  displayOrder: number;
  isFeatured: boolean;
  createdAt: string;
}

export interface UploadedMedia {
  _id?: string;
  type: 'video' | 'image';
  filename: string;
  url: string;
  thumbnailUrl?: string;
  fileSize: number;
  mimeType: string;
  viewCount?: number;
  durationSeconds?: number;
  uploadedAt?: string;
}

export type MediaUploadPurpose = 'post-image' | 'reel-video' | 'message-attachment' | 'live-preview';
export type MediaUploadStrategy = 'single-put' | 'multipart';

export interface MediaUploadSessionRecord {
  id: string;
  purpose: MediaUploadPurpose;
  strategy: MediaUploadStrategy;
  fileName: string;
  contentType: string;
  fileSize: number;
  objectKey: string;
  publicUrl: string;
  expiresAt: string;
}

export interface CompletedMultipartUploadPart {
  partNumber: number;
  etag: string;
}

export interface CreateMediaUploadSessionPayload {
  purpose: MediaUploadPurpose;
  fileName?: string;
  contentType: string;
  fileSize: number;
}

export interface MediaUploadSessionData {
  uploadSession: MediaUploadSessionRecord;
  upload: {
    strategy: MediaUploadStrategy;
    uploadUrl?: string;
    headers?: Record<string, string>;
    partSize?: number;
    totalParts?: number;
  };
}

interface CreateMediaUploadSessionResponse {
  success: boolean;
  uploadSession: MediaUploadSessionRecord;
  upload: MediaUploadSessionData['upload'];
}

interface MultipartUploadPartPresign {
  partNumber: number;
  uploadUrl: string;
  headers: Record<string, string>;
}

interface CreateMultipartUploadPartPresignsResponse {
  success: boolean;
  uploadSessionId: string;
  partSize: number;
  parts: MultipartUploadPartPresign[];
}

interface CreatePostResponse {
  success: boolean;
  post: FeedPost;
}

interface RepostPostResponse {
  success: boolean;
  post: FeedPost;
}

interface TogglePostLikeResponse {
  success: boolean;
  post: FeedPost;
}

interface SetPostReactionResponse {
  success: boolean;
  post: FeedPost;
}

interface CreateCommentResponse {
  success: boolean;
  comment: FeedComment;
  commentCount: number;
}

interface DeleteCommentResponse {
  success: boolean;
  commentCount: number;
}

interface IncrementHomeReelViewResponse {
  success: boolean;
  reel: HomeReel;
}

interface ToggleHomeReelLikeResponse {
  success: boolean;
  reel: HomeReel;
}

interface UploadMediaResponse {
  success: boolean;
  media: UploadedMedia;
}

interface CreateHighlightReelResponse {
  success: boolean;
  reel: HighlightReelRecord;
}

interface PresignedPostImageUploadResponse {
  success: boolean;
  uploadUrl: string;
  objectKey: string;
  publicUrl: string;
  headers: Record<string, string>;
}

type ApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

const socialFeedClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const IMAGE_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;
const MULTIPART_UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;
const MULTIPART_UPLOAD_CONCURRENCY = 4;

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.message.toLowerCase().includes('timeout')) {
      return `${fallbackMessage}. The upload took too long and timed out. Please try again.`;
    }

    const errorData = error.response?.data as ApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

const normalizeProgressFraction = (loaded: number, total: number) => {
  if (!Number.isFinite(loaded) || !Number.isFinite(total) || total <= 0) {
    return 0;
  }

  return Math.min(Math.max(loaded / total, 0), 1);
};

const getResponseHeader = (headers: Record<string, string | undefined>, name: string) => {
  const directValue = headers[name];
  if (directValue) {
    return directValue;
  }

  const lowerCaseName = name.toLowerCase();
  const matchingHeaderName = Object.keys(headers).find((headerName) => headerName.toLowerCase() === lowerCaseName);
  return matchingHeaderName ? headers[matchingHeaderName] : undefined;
};

export const socialFeedApi = {
  async getHomeStats(token: string, signal?: AbortSignal): Promise<HomeStatsResponse> {
    try {
      const response = await socialFeedClient.get<HomeStatsResponse>('/home/stats', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load home stats'));
    }
  },

  async getTrendingTopics(token: string, signal?: AbortSignal): Promise<TrendingTopicsResponse> {
    try {
      const response = await socialFeedClient.get<TrendingTopicsResponse>('/home/trending-topics', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {limit: 4},
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load trending topics'));
    }
  },

  async deletePost(token: string, postId: string): Promise<void> {
    try {
      const response = await socialFeedClient.delete<{success: boolean}>(`/posts/${encodeURIComponent(postId)}`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      if (!response.data.success) {
        throw new Error('Failed to delete post');
      }
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to delete post'));
    }
  },

  async getPosts(token: string, options?: GetPostsOptions, signal?: AbortSignal): Promise<FeedResponse> {
    try {
      const response = await socialFeedClient.get<FeedResponse>('/posts', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          ...(options?.cursor ? {cursor: options.cursor} : {}),
          ...(typeof options?.limit === 'number' ? {limit: options.limit} : {}),
          ...(options?.topic ? {topic: options.topic} : {}),
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load posts'));
    }
  },

  async getPost(token: string, postId: string, signal?: AbortSignal): Promise<GetPostResponse> {
    try {
      const response = await socialFeedClient.get<GetPostResponse>(`/posts/${postId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load the post'));
    }
  },

  async createPost(token: string, payload: CreatePostPayload, signal?: AbortSignal): Promise<CreatePostResponse> {
    try {
      const response = await socialFeedClient.post<CreatePostResponse>('/posts', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to create post'));
    }
  },

  async repostPost(
    token: string,
    postId: string,
    payload: RepostPostPayload,
    signal?: AbortSignal
  ): Promise<RepostPostResponse> {
    try {
      const response = await socialFeedClient.post<RepostPostResponse>(`/posts/${postId}/repost`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to repost blog'));
    }
  },

  async togglePostLike(token: string, postId: string, signal?: AbortSignal): Promise<TogglePostLikeResponse> {
    try {
      const response = await socialFeedClient.post<TogglePostLikeResponse>(`/posts/${postId}/like`, undefined, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to update like'));
    }
  },

  async setPostReaction(
    token: string,
    postId: string,
    reactionType: PostReactionType | null,
    signal?: AbortSignal
  ): Promise<SetPostReactionResponse> {
    try {
      const response = await socialFeedClient.post<SetPostReactionResponse>(
        `/posts/${postId}/reaction`,
        {reactionType},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to update reaction'));
    }
  },

  async getPostComments(token: string, postId: string, signal?: AbortSignal): Promise<FeedCommentsResponse> {
    try {
      const response = await socialFeedClient.get<FeedCommentsResponse>(`/posts/${postId}/comments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load comments'));
    }
  },

  async createPostComment(
    token: string,
    postId: string,
    content: string,
    signal?: AbortSignal
  ): Promise<CreateCommentResponse> {
    try {
      const response = await socialFeedClient.post<CreateCommentResponse>(
        `/posts/${postId}/comments`,
        {content},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to post comment'));
    }
  },

  async deletePostComment(token: string, commentId: string, signal?: AbortSignal): Promise<DeleteCommentResponse> {
    try {
      const response = await socialFeedClient.delete<DeleteCommentResponse>(`/comments/${commentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to delete comment'));
    }
  },

  async getHomeReels(token: string, options?: GetHomeReelsOptions, signal?: AbortSignal): Promise<HomeReelsResponse> {
    try {
      const response = await socialFeedClient.get<HomeReelsResponse>('/media/highlight-reels/feed', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          ...(options?.cursor ? {cursor: options.cursor} : {}),
          ...(typeof options?.limit === 'number' ? {limit: options.limit} : {}),
          ...(options?.view ? {view: options.view} : {}),
          ...(options?.search?.trim() ? {search: options.search.trim()} : {}),
          ...(options?.topic ? {topic: options.topic} : {}),
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load reels'));
    }
  },

  async getHighlightReel(token: string, reelId: string, signal?: AbortSignal): Promise<GetHighlightReelResponse> {
    try {
      const response = await socialFeedClient.get<GetHighlightReelResponse>(`/media/highlight-reels/${reelId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load the reel'));
    }
  },

  async getReelShareRecipients(
    token: string,
    reelId: string,
    options: {query?: string; cursor?: string; limit?: number} = {},
    signal?: AbortSignal,
  ): Promise<{recipients: ReelShareRecipient[]; nextCursor?: string}> {
    try {
      const response = await socialFeedClient.get<{success: boolean; recipients: ReelShareRecipient[]; nextCursor?: string}>(
        `/media/highlight-reels/${reelId}/share-recipients`,
        {
          headers: {Authorization: `Bearer ${token}`},
          params: {q: options.query, cursor: options.cursor, limit: options.limit},
          signal,
        },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') throw error;
      throw new Error(getErrorMessage(error, 'Failed to load coaches'));
    }
  },

  async shareReelWithCoach(
    token: string,
    reelId: string,
    payload: {recipientId: string; note?: string},
    signal?: AbortSignal,
  ): Promise<ReelShareDelivery> {
    try {
      const response = await socialFeedClient.post<{success: boolean; delivery: ReelShareDelivery}>(
        `/media/highlight-reels/${reelId}/shares`,
        payload,
        {headers: {Authorization: `Bearer ${token}`}, signal},
      );
      return response.data.delivery;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') throw error;
      throw new Error(getErrorMessage(error, 'Failed to send this reel'));
    }
  },

  async incrementHomeReelView(token: string, reelId: string, signal?: AbortSignal): Promise<IncrementHomeReelViewResponse> {
    try {
      const response = await socialFeedClient.post<IncrementHomeReelViewResponse>(`/media/highlight-reels/${reelId}/view`, undefined, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to update reel views'));
    }
  },

  async toggleHomeReelLike(token: string, reelId: string, signal?: AbortSignal): Promise<ToggleHomeReelLikeResponse> {
    try {
      const response = await socialFeedClient.post<ToggleHomeReelLikeResponse>(`/media/highlight-reels/${reelId}/like`, undefined, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to update reel like'));
    }
  },

  async getReelComments(token: string, reelId: string, signal?: AbortSignal): Promise<FeedCommentsResponse> {
    try {
      const response = await socialFeedClient.get<FeedCommentsResponse>(`/media/highlight-reels/${reelId}/comments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to load comments'));
    }
  },

  async createReelComment(
    token: string,
    reelId: string,
    content: string,
    signal?: AbortSignal
  ): Promise<CreateCommentResponse> {
    try {
      const response = await socialFeedClient.post<CreateCommentResponse>(
        `/media/highlight-reels/${reelId}/comments`,
        {content},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to post comment'));
    }
  },

  async deleteReelComment(token: string, commentId: string, signal?: AbortSignal): Promise<DeleteCommentResponse> {
    try {
      const response = await socialFeedClient.delete<DeleteCommentResponse>(`/media/highlight-reel-comments/${commentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to delete comment'));
    }
  },

  async createMediaUploadSession(
    token: string,
    payload: CreateMediaUploadSessionPayload,
    signal?: AbortSignal
  ): Promise<MediaUploadSessionData> {
    try {
      const response = await socialFeedClient.post<CreateMediaUploadSessionResponse>('/media/uploads', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return {
        uploadSession: response.data.uploadSession,
        upload: response.data.upload,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to create upload session'));
    }
  },

  async createMultipartUploadPartPresigns(
    token: string,
    uploadSessionId: string,
    partNumbers: number[],
    signal?: AbortSignal
  ): Promise<CreateMultipartUploadPartPresignsResponse> {
    try {
      const response = await socialFeedClient.post<CreateMultipartUploadPartPresignsResponse>(
        `/media/uploads/${uploadSessionId}/parts/presign`,
        {partNumbers},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to prepare multipart upload'));
    }
  },

  async abortMediaUploadSession(token: string, uploadSessionId: string, signal?: AbortSignal): Promise<void> {
    try {
      await socialFeedClient.post(`/media/uploads/${uploadSessionId}/abort`, undefined, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to abort upload session'));
    }
  },

  async uploadSinglePutSessionFile(
    sessionData: MediaUploadSessionData,
    file: File,
    options?: {
      signal?: AbortSignal;
      onProgress?: (fraction: number) => void;
    }
  ): Promise<void> {
    if (sessionData.upload.strategy !== 'single-put' || !sessionData.upload.uploadUrl) {
      throw new Error('This upload session does not support direct single PUT uploads.');
    }

    await axios.put(sessionData.upload.uploadUrl, file, {
      timeout: IMAGE_UPLOAD_TIMEOUT_MS,
      headers: sessionData.upload.headers,
      signal: options?.signal,
      onUploadProgress: (event) => {
        options?.onProgress?.(normalizeProgressFraction(event.loaded || 0, file.size));
      },
    });
  },

  async uploadMultipartSessionFile(
    token: string,
    sessionData: MediaUploadSessionData,
    file: File,
    options?: {
      signal?: AbortSignal;
      onProgress?: (fraction: number) => void;
    }
  ): Promise<CompletedMultipartUploadPart[]> {
    if (
      sessionData.upload.strategy !== 'multipart' ||
      !sessionData.upload.partSize ||
      !sessionData.upload.totalParts
    ) {
      throw new Error('This upload session does not support multipart uploads.');
    }

    const partNumbers = Array.from(
      {length: sessionData.upload.totalParts},
      (_, index) => index + 1
    );
    const presigns = await this.createMultipartUploadPartPresigns(
      token,
      sessionData.uploadSession.id,
      partNumbers,
      options?.signal
    );

    const partSize = sessionData.upload.partSize;
    const uploadedPartBytes = new Array(partNumbers.length).fill(0);
    const completedParts: CompletedMultipartUploadPart[] = [];
    let nextPartIndex = 0;

    const reportProgress = () => {
      const totalUploadedBytes = uploadedPartBytes.reduce((sum, value) => sum + value, 0);
      options?.onProgress?.(normalizeProgressFraction(totalUploadedBytes, file.size));
    };

    const uploadPart = async () => {
      while (nextPartIndex < presigns.parts.length) {
        const partIndex = nextPartIndex;
        nextPartIndex += 1;
        const part = presigns.parts[partIndex];
        const startOffset = (part.partNumber - 1) * partSize;
        const endOffset = Math.min(startOffset + partSize, file.size);
        const chunk = file.slice(startOffset, endOffset);

        const response = await axios.put(part.uploadUrl, chunk, {
          timeout: MULTIPART_UPLOAD_TIMEOUT_MS,
          headers: part.headers,
          signal: options?.signal,
          onUploadProgress: (event) => {
            uploadedPartBytes[partIndex] = Math.min(event.loaded || 0, chunk.size);
            reportProgress();
          },
        });

        uploadedPartBytes[partIndex] = chunk.size;
        reportProgress();

        const etag = getResponseHeader(response.headers as Record<string, string | undefined>, 'etag');

        if (!etag) {
          throw new Error(
            'The upload completed without an ETag response header. Ensure your S3 CORS configuration exposes ETag.'
          );
        }

        completedParts.push({
          partNumber: part.partNumber,
          etag,
        });
      }
    };

    const workers = Array.from(
      {length: Math.min(MULTIPART_UPLOAD_CONCURRENCY, presigns.parts.length)},
      () => uploadPart()
    );

    await Promise.all(workers);

    return completedParts.sort((left, right) => left.partNumber - right.partNumber);
  },

  async uploadPostImage(token: string, file: File, signal?: AbortSignal): Promise<UploadMediaResponse> {
    try {
      const presignedUpload = await socialFeedClient.post<PresignedPostImageUploadResponse>('/media/upload/presign', {
        contentType: file.type,
        fileName: file.name,
        fileSize: file.size,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      await axios.put(presignedUpload.data.uploadUrl, file, {
        timeout: IMAGE_UPLOAD_TIMEOUT_MS,
        headers: presignedUpload.data.headers,
        signal,
      });

      const completedUpload = await socialFeedClient.post<UploadMediaResponse>('/media/upload/complete', {
        objectKey: presignedUpload.data.objectKey,
        contentType: file.type,
        fileSize: file.size,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return completedUpload.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to upload image'));
    }
  },

  async uploadReelVideo(
    token: string,
    file: File,
    durationSeconds?: number,
    signal?: AbortSignal
  ): Promise<UploadMediaResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (typeof durationSeconds === 'number' && Number.isFinite(durationSeconds) && durationSeconds > 0) {
        formData.append('durationSeconds', String(Math.round(durationSeconds)));
      }

      const response = await socialFeedClient.post<UploadMediaResponse>('/media/upload', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 15 * 60 * 1000,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to upload reel'));
    }
  },

  async createHighlightReel(
    token: string,
    payload: CreateHighlightReelPayload,
    signal?: AbortSignal
  ): Promise<CreateHighlightReelResponse> {
    try {
      const response = await socialFeedClient.post<CreateHighlightReelResponse>('/media/highlight-reels', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to create highlight reel'));
    }
  },

  async deleteMedia(token: string, mediaId: string, signal?: AbortSignal): Promise<void> {
    try {
      await socialFeedClient.delete(`/media/${mediaId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new Error(getErrorMessage(error, 'Failed to delete media'));
    }
  },
};
